import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Button,
  Checkbox,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  Stack,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  TaskAltOutlined as TaskIcon,
  InboxOutlined as InboxIcon,
  WarningAmberOutlined as OverdueIcon,
  TodayOutlined as TodayIcon,
  ListAltOutlined as ListIcon,
  CheckCircleOutlineOutlined as DoneIcon,
  DeleteOutline as DeleteIcon,
  SnoozeOutlined as SnoozeIcon,
  KeyboardOutlined as KeyboardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import {
  format,
  isPast,
  isToday,
  isTomorrow,
  isThisWeek,
  startOfDay,
  addDays,
  addWeeks,
  setHours,
  setMinutes,
  isAfter,
} from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { taskApi, customerApi } from '../api/client';
import { Task, Priority } from '../types';
import PageHeader from '../components/layout/PageHeader';
import TaskDrawer from '../components/tasks/TaskDrawer';

type TabKey = 'overdue' | 'today' | 'open' | 'done';
type SortField = 'title' | 'customer' | 'priority' | 'dueDate';
type SortOrder = 'asc' | 'desc';

const priorityStyle: Record<
  Priority,
  { label: string; color: string; tint: string; border: string; weight: number }
> = {
  LOW: { label: 'Lav', color: '#5C6B63', tint: '#EDF0EE', border: '#D1DCD5', weight: 0 },
  MEDIUM: { label: 'Medium', color: '#3E7A5A', tint: '#E7F2EC', border: '#C8DDD1', weight: 1 },
  HIGH: { label: 'Høj', color: '#8A5A10', tint: '#FBF1DE', border: '#F0D9A6', weight: 2 },
  URGENT: { label: 'Urgent', color: '#8A2E24', tint: '#FBEAE8', border: '#F0C2BE', weight: 3 },
};

const PriorityPill: React.FC<{ priority: Priority }> = ({ priority }) => {
  const s = priorityStyle[priority] || priorityStyle.MEDIUM;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{
        bgcolor: s.tint,
        color: s.color,
        border: `1px solid ${s.border}`,
        fontWeight: 600,
        height: 20,
        fontSize: '0.65rem',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

const smartQuickAddDate = (): Date => {
  const now = new Date();
  // If after 16:00, schedule for next day at 10:00
  if (now.getHours() >= 16) {
    let next = addDays(now, 1);
    // Skip weekend to next Monday
    while (next.getDay() === 0 || next.getDay() === 6) next = addDays(next, 1);
    return setMinutes(setHours(next, 10), 0);
  }
  // Otherwise today at 17:00
  return setMinutes(setHours(now, 17), 0);
};

const groupTasksByDate = (tasks: Task[]) => {
  const groups: Record<string, Task[]> = {
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
  };
  tasks.forEach((t) => {
    const d = new Date(t.dueDate);
    const day = startOfDay(d);
    if (!t.isCompleted && isPast(day) && !isToday(day)) groups.overdue.push(t);
    else if (isToday(d)) groups.today.push(t);
    else if (isTomorrow(d)) groups.tomorrow.push(t);
    else if (isThisWeek(d, { weekStartsOn: 1 })) groups.thisWeek.push(t);
    else groups.later.push(t);
  });
  return groups;
};

const groupOrder: { key: keyof ReturnType<typeof groupTasksByDate>; label: string; accent: string }[] = [
  { key: 'overdue', label: 'Forfaldne', accent: '#C74A3E' },
  { key: 'today', label: 'I dag', accent: '#208040' },
  { key: 'tomorrow', label: 'I morgen', accent: '#3E7A5A' },
  { key: 'thisWeek', label: 'Denne uge', accent: '#60A080' },
  { key: 'later', label: 'Senere', accent: '#9CA9A1' },
];

export const Tasks: React.FC = () => {
  const queryClient = useQueryClient();
  const [tabKey, setTabKey] = useState<TabKey>('today');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<Priority | ''>('');
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quickTitle, setQuickTitle] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerTask, setDrawerTask] = useState<Task | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; undo?: () => void } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM' as Priority,
    dueDate: smartQuickAddDate(),
    customerId: '',
  });

  const showCompleted = tabKey === 'done';
  const showToday = tabKey === 'today';
  const showOverdue = tabKey === 'overdue';

  const { data, isLoading, error } = useQuery({
    queryKey: ['tasks', { tabKey, page, rowsPerPage, search }],
    queryFn: async () => {
      if (showToday) {
        const res = await taskApi.getToday();
        return { data: res.data.data as Task[], pagination: { total: res.data.data?.length || 0 } };
      }
      if (showOverdue) {
        const res = await taskApi.getOverdue();
        return { data: res.data.data as Task[], pagination: { total: res.data.data?.length || 0 } };
      }
      const res = await taskApi.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        showCompleted,
        assignedToMe: true,
      });
      return res.data as { data: Task[]; pagination: { total: number } };
    },
  });

  const { data: countsData, refetch: refetchCounts } = useQuery({
    queryKey: ['tasks', 'counts'],
    queryFn: async () => {
      const [today, overdue, all] = await Promise.all([
        taskApi.getToday(),
        taskApi.getOverdue(),
        taskApi.getAll({ limit: 1, showCompleted: false, assignedToMe: true }),
      ]);
      return {
        today: today.data.data?.length || 0,
        overdue: overdue.data.data?.length || 0,
        open: all.data?.pagination?.total || 0,
      };
    },
    staleTime: 5000,
  });

  const { data: customers } = useQuery({
    queryKey: ['customers', 'simple'],
    queryFn: async () => {
      const res = await customerApi.getAll({ limit: 200 });
      return res.data.data;
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    refetchCounts();
  };

  const createMutation = useMutation({
    mutationFn: (payload: any) => taskApi.create(payload),
    onSuccess: () => {
      invalidate();
      handleCloseDialog();
      setQuickTitle('');
      setSnackbar({ msg: 'Opgave oprettet' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => taskApi.update(id, data),
    onSuccess: (res) => {
      invalidate();
      if (drawerTask) {
        setDrawerTask({ ...drawerTask, ...(res.data?.data || {}) });
      }
    },
  });

  // Optimistic complete/uncomplete
  const completeMutation = useMutation({
    mutationFn: (task: Task) =>
      task.isCompleted ? taskApi.uncomplete(task.id) : taskApi.complete(task.id),
    onMutate: async (task) => {
      await queryClient.cancelQueries({ queryKey: ['tasks'] });
      const previous = queryClient.getQueriesData<any>({ queryKey: ['tasks'] });
      queryClient.setQueriesData<any>({ queryKey: ['tasks'] }, (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((t: Task) =>
            t.id === task.id ? { ...t, isCompleted: !t.isCompleted } : t
          ),
        };
      });
      return { previous };
    },
    onError: (_e, _task, ctx: any) => {
      if (ctx?.previous) ctx.previous.forEach(([k, v]: any) => queryClient.setQueryData(k, v));
      setSnackbar({ msg: 'Kunne ikke opdatere opgave' });
    },
    onSettled: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => taskApi.delete(id),
    onSuccess: () => {
      invalidate();
      setDrawerTask(null);
      setSnackbar({ msg: 'Opgave slettet' });
    },
  });

  // Snooze: updates dueDate directly
  const snoozeMutation = useMutation({
    mutationFn: ({ task, by }: { task: Task; by: 'day' | 'week' }) => {
      const newDate =
        by === 'day' ? addDays(new Date(task.dueDate), 1) : addWeeks(new Date(task.dueDate), 1);
      return taskApi.update(task.id, { dueDate: newDate.toISOString() });
    },
    onSuccess: (_, vars) => {
      invalidate();
      setSnackbar({
        msg: `Opgave udskudt ${vars.by === 'day' ? '1 dag' : '1 uge'}`,
      });
    },
  });

  // Bulk operations
  const bulkCompleteMutation = useMutation({
    mutationFn: async (ids: string[]) => Promise.all(ids.map((id) => taskApi.complete(id))),
    onSuccess: () => {
      invalidate();
      setSelected(new Set());
      setSnackbar({ msg: 'Opgaver fuldført' });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => Promise.all(ids.map((id) => taskApi.delete(id))),
    onSuccess: () => {
      invalidate();
      setSelected(new Set());
      setSnackbar({ msg: 'Opgaver slettet' });
    },
  });

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setFormData({
      title: '',
      description: '',
      priority: 'MEDIUM',
      dueDate: smartQuickAddDate(),
      customerId: '',
    });
  }, []);

  const handleSubmitDialog = () => {
    createMutation.mutate({
      ...formData,
      dueDate: formData.dueDate.toISOString(),
      customerId: formData.customerId || undefined,
    });
  };

  const handleQuickAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    createMutation.mutate({
      title: quickTitle.trim(),
      priority: 'MEDIUM',
      dueDate: smartQuickAddDate().toISOString(),
    });
  };

  // Client-side filtering + sorting
  const rawTasks = data?.data || [];
  const processedTasks = useMemo(() => {
    let arr = rawTasks;
    if (priorityFilter) arr = arr.filter((t) => t.priority === priorityFilter);
    const order = sortOrder === 'asc' ? 1 : -1;
    arr = [...arr].sort((a, b) => {
      switch (sortField) {
        case 'title':
          return order * a.title.localeCompare(b.title, 'da');
        case 'customer':
          return order * ((a.customer?.name || '').localeCompare(b.customer?.name || '', 'da'));
        case 'priority':
          return order * (priorityStyle[a.priority].weight - priorityStyle[b.priority].weight);
        case 'dueDate':
        default:
          return order * (new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
      }
    });
    return arr;
  }, [rawTasks, priorityFilter, sortField, sortOrder]);

  const grouped = useMemo(() => groupTasksByDate(processedTasks), [processedTasks]);
  const counts = countsData || { today: 0, overdue: 0, open: 0 };
  const subtitleText = isLoading
    ? 'Henter opgaver…'
    : `${counts.overdue} forfaldne · ${counts.today} i dag · ${counts.open} åbne i alt`;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === processedTasks.length) setSelected(new Set());
    else setSelected(new Set(processedTasks.map((t) => t.id)));
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (e.key === 'Escape') {
        if (drawerTask) setDrawerTask(null);
        else if (dialogOpen) handleCloseDialog();
        else if (helpOpen) setHelpOpen(false);
        else if (selected.size > 0) setSelected(new Set());
        return;
      }
      if (isTyping) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        setDialogOpen(true);
      } else if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === '?') {
        setHelpOpen(true);
      } else if (e.key === '1') setTabKey('overdue');
      else if (e.key === '2') setTabKey('today');
      else if (e.key === '3') setTabKey('open');
      else if (e.key === '4') setTabKey('done');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [drawerTask, dialogOpen, helpOpen, selected.size, handleCloseDialog]);

  const renderRow = (task: Task) => {
    const overdue = !task.isCompleted && isPast(new Date(task.dueDate)) && !isToday(new Date(task.dueDate));
    const isSelected = selected.has(task.id);
    return (
      <TableRow
        key={task.id}
        hover
        selected={isSelected}
        onClick={() => setDrawerTask(task)}
        sx={{
          cursor: 'pointer',
          opacity: task.isCompleted ? 0.55 : 1,
          '& td': task.isCompleted ? { textDecoration: 'line-through' } : undefined,
        }}
      >
        <TableCell
          padding="checkbox"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => toggleSelect(task.id)}
            size="small"
            sx={{ color: '#60A080', '&.Mui-checked': { color: '#208040' } }}
          />
        </TableCell>
        <TableCell
          padding="checkbox"
          onClick={(e) => {
            e.stopPropagation();
            completeMutation.mutate(task);
          }}
        >
          <Checkbox
            checked={task.isCompleted}
            size="small"
            icon={<DoneIcon sx={{ fontSize: 18, color: '#9CA9A1' }} />}
            checkedIcon={<DoneIcon sx={{ fontSize: 18, color: '#208040' }} />}
          />
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
            {task.title}
          </Typography>
          {task.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.6875rem', mt: 0.25 }}
            >
              {task.description.length > 60
                ? task.description.substring(0, 60) + '…'
                : task.description}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {task.customer ? (
            <Typography
              component={RouterLink}
              to={`/customers/${task.customer.id}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontSize: '0.75rem',
                color: '#208040',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline', color: '#16602F' },
              }}
            >
              {task.customer.name}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <PriorityPill priority={task.priority} />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: overdue ? 600 : 400,
                color: overdue ? '#C74A3E' : 'text.primary',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {format(new Date(task.dueDate), 'dd. MMM HH:mm', { locale: da })}
            </Typography>
            {!task.isCompleted && (
              <Tooltip title="Udskyd 1 dag">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    snoozeMutation.mutate({ task, by: 'day' });
                  }}
                  sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: '#208040' } }}
                >
                  <SnoozeIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderGroupedBody = () => {
    const nonEmpty = groupOrder.filter((g) => grouped[g.key].length > 0);
    if (nonEmpty.length === 0) return null;
    return nonEmpty.flatMap((g) => [
      <TableRow key={`h-${g.key}`} hover={false}>
        <TableCell
          colSpan={6}
          sx={{ bgcolor: '#F6FAF7', py: 0.5, borderBottom: '1px solid #E3ECE6' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: g.accent }} />
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.65rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
              }}
            >
              {g.label}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              · {grouped[g.key].length}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>,
      ...grouped[g.key].map(renderRow),
    ]);
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <TableRow key={`sk-${i}`} hover={false}>
        <TableCell padding="checkbox">
          <Skeleton variant="circular" width={18} height={18} />
        </TableCell>
        <TableCell padding="checkbox">
          <Skeleton variant="circular" width={18} height={18} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="60%" height={16} />
          <Skeleton variant="text" width="40%" height={12} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="70%" height={14} />
        </TableCell>
        <TableCell>
          <Skeleton variant="rounded" width={52} height={20} />
        </TableCell>
        <TableCell align="right">
          <Skeleton variant="text" width={80} height={14} />
        </TableCell>
      </TableRow>
    ));

  const renderEmpty = () => (
    <TableRow>
      <TableCell colSpan={6} sx={{ py: 5, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={1}>
          {tabKey === 'overdue' ? (
            <DoneIcon sx={{ fontSize: 36, color: '#60A080' }} />
          ) : (
            <InboxIcon sx={{ fontSize: 36, color: '#60A080' }} />
          )}
          <Typography variant="body2" color="text.secondary">
            {tabKey === 'overdue'
              ? 'Ingen forfaldne opgaver — godt gået'
              : tabKey === 'today'
                ? 'Ingen opgaver i dag'
                : tabKey === 'done'
                  ? 'Ingen fuldførte opgaver endnu'
                  : 'Ingen åbne opgaver'}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const useGrouping = tabKey === 'open' && !showCompleted && !priorityFilter && sortField === 'dueDate';

  const allSelected = selected.size > 0 && selected.size === processedTasks.length;
  const someSelected = selected.size > 0 && selected.size < processedTasks.length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box>
        <PageHeader
          title="Opgaver"
          subtitle={subtitleText}
          icon={<TaskIcon sx={{ fontSize: 18 }} />}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Tastaturgenveje (?)">
                <IconButton
                  size="small"
                  onClick={() => setHelpOpen(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <KeyboardIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => setDialogOpen(true)}
              >
                Ny opgave
              </Button>
            </Stack>
          }
        />

        {/* Tabs */}
        <Card sx={{ mb: 1.5 }}>
          <Tabs
            value={tabKey}
            onChange={(_, v) => {
              setTabKey(v);
              setPage(0);
              setSelected(new Set());
            }}
            sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 0.5 } }}
          >
            <Tab
              value="overdue"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <OverdueIcon sx={{ fontSize: 14 }} />
                  Forfaldne
                  {counts.overdue > 0 && (
                    <Chip
                      label={counts.overdue}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.625rem',
                        bgcolor: '#FBEAE8',
                        color: '#8A2E24',
                        fontWeight: 700,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              value="today"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TodayIcon sx={{ fontSize: 14 }} />I dag
                  {counts.today > 0 && (
                    <Chip
                      label={counts.today}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.625rem',
                        bgcolor: '#DDEEE2',
                        color: '#16602F',
                        fontWeight: 700,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              value="open"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ListIcon sx={{ fontSize: 14 }} />
                  Alle åbne
                </Box>
              }
            />
            <Tab
              value="done"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <DoneIcon sx={{ fontSize: 14 }} />
                  Fuldførte
                </Box>
              }
            />
          </Tabs>
        </Card>

        {/* Quick add — only for open + today */}
        {(tabKey === 'today' || tabKey === 'open') && (
          <Card sx={{ mb: 1.5, p: 1 }}>
            <Box
              component="form"
              onSubmit={handleQuickAdd}
              sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
            >
              <AddIcon sx={{ fontSize: 18, color: '#60A080', ml: 0.5 }} />
              <TextField
                size="small"
                placeholder={
                  isAfter(new Date(), setHours(new Date(), 16))
                    ? 'Tilføj hurtigt en opgave — deadline næste arbejdsdag kl 10'
                    : 'Tilføj hurtigt en opgave — deadline i dag kl 17'
                }
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                fullWidth
                variant="standard"
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
              />
              {quickTitle && (
                <Button
                  size="small"
                  type="submit"
                  variant="contained"
                  disabled={createMutation.isPending}
                  sx={{ flexShrink: 0 }}
                >
                  Tilføj
                </Button>
              )}
            </Box>
          </Card>
        )}

        {/* Filter bar */}
        <Card sx={{ mb: 1.5, p: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              inputRef={searchRef}
              placeholder="Søg opgaver… (/)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Prioritet</InputLabel>
              <Select
                value={priorityFilter}
                label="Prioritet"
                onChange={(e) => setPriorityFilter(e.target.value as Priority | '')}
              >
                <MenuItem value="">Alle prioriteter</MenuItem>
                <MenuItem value="URGENT">Urgent</MenuItem>
                <MenuItem value="HIGH">Høj</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Lav</MenuItem>
              </Select>
            </FormControl>
            {(priorityFilter || search) && (
              <Button
                size="small"
                onClick={() => {
                  setPriorityFilter('');
                  setSearch('');
                }}
                sx={{ textTransform: 'none' }}
              >
                Ryd filtre
              </Button>
            )}
          </Box>
        </Card>

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <Card
            sx={{
              mb: 1.5,
              p: 1,
              bgcolor: '#EAF4ED',
              border: '1px solid #C0E0C0',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#16602F', ml: 0.5 }}>
              {selected.size} valgt
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneIcon sx={{ fontSize: 16 }} />}
              onClick={() => bulkCompleteMutation.mutate(Array.from(selected))}
              disabled={bulkCompleteMutation.isPending}
            >
              Fuldfør
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                if (window.confirm(`Slet ${selected.size} opgaver?`))
                  bulkDeleteMutation.mutate(Array.from(selected));
              }}
              disabled={bulkDeleteMutation.isPending}
              sx={{ color: '#8A2E24', borderColor: '#F0C2BE' }}
            >
              Slet
            </Button>
            <IconButton size="small" onClick={() => setSelected(new Set())}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Card>
        )}

        {/* Table */}
        <Card>
          {error && (
            <Alert severity="error" sx={{ m: 1.5 }}>
              Kunne ikke hente opgaver
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      sx={{ color: '#60A080', '&.Mui-checked': { color: '#208040' } }}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'title'}
                      direction={sortField === 'title' ? sortOrder : 'asc'}
                      onClick={() => toggleSort('title')}
                    >
                      Opgave
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'customer'}
                      direction={sortField === 'customer' ? sortOrder : 'asc'}
                      onClick={() => toggleSort('customer')}
                    >
                      Kunde
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'priority'}
                      direction={sortField === 'priority' ? sortOrder : 'asc'}
                      onClick={() => toggleSort('priority')}
                    >
                      Prioritet
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'dueDate'}
                      direction={sortField === 'dueDate' ? sortOrder : 'asc'}
                      onClick={() => toggleSort('dueDate')}
                    >
                      Forfald
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading
                  ? renderSkeletonRows()
                  : processedTasks.length === 0
                    ? renderEmpty()
                    : useGrouping
                      ? renderGroupedBody()
                      : processedTasks.map(renderRow)}
              </TableBody>
            </Table>
          </TableContainer>
          {(tabKey === 'open' || tabKey === 'done') && (
            <TablePagination
              component="div"
              count={data?.pagination?.total || 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rækker pr. side:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} af ${count}`}
            />
          )}
        </Card>

        {/* Task detail drawer */}
        <TaskDrawer
          open={!!drawerTask}
          task={drawerTask}
          customers={customers}
          onClose={() => setDrawerTask(null)}
          onSave={(id, payload) => updateMutation.mutate({ id, data: payload })}
          onDelete={(id) => deleteMutation.mutate(id)}
          onToggleComplete={(task) => {
            completeMutation.mutate(task);
            setDrawerTask({ ...task, isCompleted: !task.isCompleted });
          }}
          onSnooze={(task, by) => snoozeMutation.mutate({ task, by })}
          saving={updateMutation.isPending}
          deleting={deleteMutation.isPending}
        />

        {/* Create dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Ny opgave</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                size="small"
                label="Titel"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
                autoFocus
              />
              <TextField
                size="small"
                label="Beskrivelse"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              <DateTimePicker
                label="Forfald"
                value={formData.dueDate}
                onChange={(date) => date && setFormData({ ...formData, dueDate: date })}
                ampm={false}
                format="dd. MMM yyyy HH:mm"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Prioritet</InputLabel>
                <Select
                  value={formData.priority}
                  label="Prioritet"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value as Priority })
                  }
                >
                  <MenuItem value="LOW">Lav</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">Høj</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Kunde (valgfri)</InputLabel>
                <Select
                  value={formData.customerId}
                  label="Kunde (valgfri)"
                  onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                >
                  <MenuItem value="">Ingen</MenuItem>
                  {customers?.map((customer: any) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuller</Button>
            <Button
              variant="contained"
              onClick={handleSubmitDialog}
              disabled={createMutation.isPending || !formData.title}
            >
              {createMutation.isPending ? 'Opretter…' : 'Opret'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Keyboard help dialog */}
        <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyboardIcon sx={{ fontSize: 18, color: '#208040' }} />
            Tastaturgenveje
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.25} sx={{ pt: 1 }}>
              {[
                ['N', 'Opret ny opgave'],
                ['/', 'Fokus søgefelt'],
                ['1 / 2 / 3 / 4', 'Skift mellem faner'],
                ['Esc', 'Luk drawer/dialog eller fjern markering'],
                ['?', 'Vis denne hjælp'],
              ].map(([key, desc]) => (
                <Box
                  key={key}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography sx={{ fontSize: '0.8125rem' }}>{desc}</Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: '#EAF4ED',
                      border: '1px solid #C0E0C0',
                      borderRadius: 0.75,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#16602F',
                    }}
                  >
                    {key}
                  </Box>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpOpen(false)}>Luk</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!snackbar}
          autoHideDuration={3500}
          onClose={() => setSnackbar(null)}
          message={snackbar?.msg}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Tasks;
